/**
 * Owner: Masa Bayu
 * File: DedicatedEarningsScreen.tsx
 * Date:Time: 2026-10-17 03:36
 * Description: Dedicated author-earnings screen — EarningLedger from SaleRecords with
 *   payout-intent controls. Purpose-built earnings UI in the profile palette.
 */

import { useState } from 'react';
import {
  getDemoEarningLedger,
  getDemoEarningsSummary,
  getDemoPayoutRequest,
  projectDemoAuthorEarnings,
  requestDemoAuthorPayout,
  resetDemoAuthorEarnings,
} from '../demo-author-earnings';
import { formatMinor } from './dedicated-format';
import './dedicated.css';

export interface DedicatedEarningsScreenProps {
  readonly onNavigate: (path: string) => void;
}

export function DedicatedEarningsScreen({ onNavigate }: DedicatedEarningsScreenProps) {
  const [status, setStatus] = useState(
    'EarningLedger derives from SaleRecords (confirmed Orders).',
  );
  const [ledger, setLedger] = useState(() => getDemoEarningLedger());
  const summary = getDemoEarningsSummary();
  const payout = getDemoPayoutRequest();

  const refresh = (nextStatus: string) => {
    setStatus(nextStatus);
    setLedger(getDemoEarningLedger());
  };

  return (
    <div className="ddx">
      <header>
        <p className="ddx__eyebrow">Author · Commerce</p>
        <h2 className="ddx__title">Earnings</h2>
        <p className="ddx__lede">
          Payout summary from Commerce-derived SaleRecords. The author UI records payout intent only
          — Stripe execution stays under Commerce.
        </p>
      </header>

      {summary !== null ? (
        <div className="ddx__stats">
          <div className="ddx__stat ddx__stat--pink">
            <span className="ddx__stat-value">
              {formatMinor(summary.pendingMinorUnits, summary.currency)}
            </span>
            <span className="ddx__stat-label">Pending</span>
          </div>
          <div className="ddx__stat ddx__stat--violet">
            <span className="ddx__stat-value">
              {formatMinor(summary.availableMinorUnits, summary.currency)}
            </span>
            <span className="ddx__stat-label">Available</span>
          </div>
          <div className="ddx__stat ddx__stat--teal">
            <span className="ddx__stat-value">
              {formatMinor(summary.payoutRequestedMinorUnits, summary.currency)}
            </span>
            <span className="ddx__stat-label">Payout requested</span>
          </div>
        </div>
      ) : null}

      <p className="ddx__status" role="status" aria-live="polite">
        {status}
        {payout !== null ? ` · last payout ${payout.state}` : ''}
      </p>

      <div className="ddx__actions">
        <button
          type="button"
          className="ddx__cta ddx__cta--solid"
          onClick={() => {
            refresh(projectDemoAuthorEarnings());
          }}
        >
          Project from sales
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            refresh(requestDemoAuthorPayout(true));
          }}
        >
          Request payout (step-up)
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            refresh(requestDemoAuthorPayout(false));
          }}
        >
          Request payout without step-up
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            resetDemoAuthorEarnings();
            refresh('Earnings cleared.');
          }}
        >
          Reset
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate('/author/sales');
          }}
        >
          Sales
        </button>
        <button
          type="button"
          className="ddx__cta"
          onClick={() => {
            onNavigate('/author');
          }}
        >
          Dashboard
        </button>
      </div>

      {ledger.length === 0 ? (
        <div className="ddx__empty">
          <h3>No earning entries yet</h3>
          <p>Project from sales to derive the ledger from confirmed SaleRecords.</p>
        </div>
      ) : (
        <div className="ddx__table-wrap">
          <table className="ddx__table" aria-label="Author earning ledger">
            <thead>
              <tr>
                <th scope="col">Order</th>
                <th scope="col">Amount</th>
                <th scope="col">State</th>
              </tr>
            </thead>
            <tbody>
              {ledger.map((entry) => (
                <tr key={entry.earningEntryId}>
                  <td>{entry.orderId}</td>
                  <td>{formatMinor(entry.amountMinorUnits, entry.currency)}</td>
                  <td>{entry.state}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
